// Explicit delta on brand's `ModalFooter` (reuse-brand consolidation,
// 2026-08-09) rather than a from-scratch redeclaration under the same key.
// Note for the next reader: every real sharedLibraries.js in this monorepo
// loads brand BEFORE shared (`[brand, shared, ...]`), and the shared-library
// merge is first-wins (smbls/packages/utils/sharedLibraries.js
// `mergeOneLibrary`: a key is copied only when the target doesn't already
// have it) — so brand's `ModalFooter` (margin: '0 -X', order: 2, align:
// 'center space-between') already wins this exact-name collision in every
// shipped context, and this file's own definition was dead weight. Live-
// verified on the workspace-ui catalog (`/shared`, ModalFooter cell): the
// rendered spec carries `align-items:center; justify-content:space-between`
// with no padding-top rule — brand's shape, not this one. `extends` makes
// that relationship explicit instead of accidental, and keeps the intended
// top-padding delta ready in case this ever renders without brand loaded.
export const ModalFooter = {
  extends: 'ModalFooter',
  padding: 'A1 0 0 0'
}

// `isOpen` reactive prop drives ALL visibility-critical CSS via a single
// reactive read of `s.root[stateKey]`. Hard `display:flex/none` is kept
// for layout (so the backdrop doesn't intercept clicks when closed) but
// `opacity` + `visibility` carry the actual fade-in/out animation, and
// pointerEvents:none on the closed state belt-and-suspenders the
// click-through guard.
export const Modal = {
  // FA204 / frank trap — syncUrl/manageFocus/maybeDismiss used to be bare
  // module-scope const/function/imperative-side-effect; they read as
  // undefined once this component is frank.toJSON'd for platform delivery —
  // platform delivery reconstructs the component from its serialized props,
  // it does not execute arbitrary top-level module code (workspace
  // d6ea1142, project_frank_drops_module_local_helpers). Fixed by keeping
  // every method in `scope` (per-instance-subtree, `el.scope.X` — see
  // create.js's scope prototype chain: own → parent.scope → … → root.scope)
  // so descendants (`Window`) reach the same methods as the Modal root.
  //
  // Escape dismissal — FA409/FA503 fix (2026-08-15): this used to be a
  // single document-level `addEventListener` bound once via
  // `window.__modalEscBound`, coordinating across every Modal instance
  // through an `el.context` "topModalEl" pointer. `window.__modalEscBound`
  // is a global-object state leak (FA409) and the raw `addEventListener`
  // bypasses the framework's own event lifecycle (FA503). Replaced with the
  // pattern `AppDialog.js` already proves live (Escape bubbling from
  // whichever descendant holds focus up to a plain `onKeydown` on the
  // dialog root): `ModalWindow` already ships `tabindex: '-1'`
  // (`company/packages/brand/components/ModalWindow.js`), so `manageFocus`
  // below moves focus onto `Window` when the modal opens (and restores it
  // to the previously-focused element on close) — that's all `onKeydown`
  // needs to receive the bubbled keydown. No cross-instance coordination is
  // needed any more: each Modal instance owns its own Escape handling, so
  // there is nothing left for a "topModalEl" pointer to arbitrate.
  scope: {
    // `display` is reactive — it re-evaluates whenever `root[stateKey]`
    // changes. We piggy-back on that to detect open/close transitions and
    // sync the URL: snapshot `location.href` on open, restore it on close.
    // Any page-level hash sync (e.g. calendar.js's `_writeHash` poll)
    // converges on the same result — if the modal's state is null again,
    // that page's hash writer won't re-add a modal marker, so the restore
    // sticks.
    syncUrl(el, isOpen) {
      if (typeof window === 'undefined') return
      if (isOpen === el._wasOpen) return
      const wasOpen = el._wasOpen
      el._wasOpen = isOpen
      if (isOpen) {
        el._urlBeforeOpen = window.location.href
      } else {
        // MOUNT is not a CLOSE: the first reactive evaluation runs with
        // `_wasOpen === undefined` and isOpen false — running the close path
        // then STRIPPED the urlParam (`?ticket=…`) off every cold-loaded
        // deep link before the modal's URL-restore could read it
        // (2026-07-25, board ?ticket links died on reload). Only a real
        // open→close transition may touch the URL.
        if (wasOpen !== true) return
        // A modal that OWNS a URL param (declares `urlParam: '<key>'`)
        // deep-links into itself — e.g. `?ticket=<id>` (2026-06-08 per Nika
        // "open ?ticket=… and close → remove from url"). For those, STRIP
        // that param on close instead of restoring the pre-open URL: a
        // deep-linked open started WITH the param, so restoring it would
        // re-add `?ticket=` on every close (and undo the modal's own
        // _clearTicketUrl). This runs for EVERY close path — backdrop, X,
        // Escape — because they all null the stateKey, which re-fires the
        // `isOpen` reactive that calls this. Modals without `urlParam` keep
        // the original snapshot/restore behavior unchanged.
        if (typeof el.urlParam === 'string' && el.urlParam) {
          try {
            const u = new URL(window.location.href)
            if (u.searchParams.has(el.urlParam)) {
              u.searchParams.delete(el.urlParam)
              window.history.replaceState(null, '', u.toString())
            }
          } catch (_) {}
        } else if (el._urlBeforeOpen && el._urlBeforeOpen !== window.location.href) {
          window.history.replaceState(null, '', el._urlBeforeOpen)
        }
        el._urlBeforeOpen = null
      }
    },
    // Moves focus onto `Window` when the modal opens (it already carries
    // `tabindex: '-1'` via brand's `ModalWindow`, so it's programmatically
    // focusable without adding anything to the tab order) and restores focus
    // to whatever was focused before the modal opened. This is what lets
    // the plain `onKeydown` below catch Escape: the key only bubbles up to
    // an ancestor's `onKeydown` from whichever element currently holds
    // focus, so without this the Modal's own `onKeydown` would never fire
    // for a mouse-only open. Deferred a frame (matches
    // `appDialogFocusPanel.js`) so the `display`/`visibility` flip has
    // already landed in the DOM before `.focus()` runs.
    manageFocus(el, isOpen) {
      if (typeof document === 'undefined') return
      if (isOpen === el._focusWasOpen) return
      el._focusWasOpen = isOpen
      if (isOpen) {
        el._returnFocusTo = document.activeElement
        const raf =
          typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (fn) => fn()
        raf(() => {
          const win = el.lookdown && el.lookdown('Window')
          if (win && win.node && typeof win.node.focus === 'function') win.node.focus()
        })
      } else {
        if (el._returnFocusTo && el._returnFocusTo.focus) el._returnFocusTo.focus()
        el._returnFocusTo = null
      }
    },
    // The shared close action — nulls `stateKey` (+ any `extraClose` merge)
    // on root state. Used by both the backdrop dismiss guard below and the
    // `onKeydown` Escape handler, so there is exactly one place that decides
    // how a Modal instance closes itself.
    close(el, s) {
      const root = s?.root
      const sk = typeof el.stateKey === 'string' ? el.stateKey : null
      if (!sk || !root?.update) return
      const extra =
        el.extraClose && typeof el.extraClose === 'object' && !Array.isArray(el.extraClose)
          ? el.extraClose
          : null
      root.update(extra ? { [sk]: null, ...extra } : { [sk]: null })
    },
    // Only direct backdrop clicks dismiss — Window's onClick below
    // stopPropagations everything inside the modal, so a click that
    // reaches here is on the backdrop layer itself.
    // THE DIALOG'S ACCESSIBLE NAME (DESIGN-WL-SHARED-MODAL-DIALOG-HAS-NO-
    // ACCESSIBLE-NAME-1). The brand ModalWindow is role=dialog + aria-modal,
    // but it never said WHICH dialog: a screen reader announced "dialog" with
    // no name. The Window now points aria-labelledby at its own heading (the
    // ModalHeader's h2, given a stable id below); a Modal with no visible
    // heading passes `ariaLabel` (on the Modal or on its Window). Never both
    // unset — a dev build warns and names the consumer.
    //
    // The Modal root is the element that holds the Window; its key names the
    // heading id (`CreateAgentModal` → `CreateAgentModalTitle`), and a
    // consumer may pin its own with `titleId`.
    modalRootOf(el) {
      let m = el
      while (m && !m.Window && m.parent) m = m.parent
      return m && m.Window ? m : null
    },
    modalTitleId(el) {
      const m = this.modalRootOf(el)
      if (!m) return null
      return m.titleId || String(m.key || 'modal').replace(/[^A-Za-z0-9_-]/g, '') + 'Title'
    },
    // A heading counts when the ModalHeader keeps its Hgroup > H and gives it
    // text (a string, a template, or a factory). Read off the definition, so
    // it answers before the children exist.
    modalHasHeading(win) {
      const h = win && win.ModalHeader && win.ModalHeader.Hgroup && win.ModalHeader.Hgroup.H
      if (!h) return false
      const t = h.text
      return typeof t === 'function' || (typeof t === 'string' && t.trim() !== '')
    },
    // `ariaLabel` from the Modal root (the Window's own flat `ariaLabel`, when a
    // consumer sets one, replaces the Window's factory at merge and wins).
    modalRootLabel(el) {
      const m = this.modalRootOf(el)
      const v = m && m.ariaLabel
      const out = typeof v === 'function' ? v(m, m.state) : v
      return out == null || out === '' ? null : out
    },
    modalWarnUnnamed(win) {
      if (typeof process === 'undefined' || process.env?.NODE_ENV === 'production') return
      if (win._modalNameWarned || this.modalHasHeading(win)) return
      const own =
        typeof win.ariaLabel === 'function' ? win.ariaLabel(win, win.state) : win.ariaLabel
      if (own != null && own !== '') return
      win._modalNameWarned = true
      const m = this.modalRootOf(win)
      console.warn(
        `[Modal] ${(m && m.key) || 'a Modal'}: the dialog has no accessible name — give its ModalHeader a heading (Hgroup > H text) or pass ariaLabel.`
      )
    },

    maybeDismiss(e, el, s) {
      if (e.target !== el.node) return
      el.scope.close(el, s)
    },
    // Is this Modal the phone bottom sheet? Yes unless its root says
    // `phoneSheet: false` — read through the root, so the root, the Window
    // and the grabber answer the same question (see `dataPhoneSheet` below).
    isPhoneSheet(el) {
      const m = this.modalRootOf(el)
      return !!m && m.phoneSheet !== false
    }
  },
  // Escape dismissal — declarative, bubbles from whichever descendant holds
  // focus (moved onto `Window` by `manageFocus` above) up to this root. No
  // document-level listener, no global/window flag, no cross-instance
  // coordination — each Modal instance owns its own Escape handling.
  onKeydown: (e, el, s) => {
    if (e.key === 'Escape') el.scope.close(el, s)
  },
  isOpen: (el, s) => {
    const open = !!s.root?.[el.stateKey]
    el.scope.syncUrl(el, open)
    el.scope.manageFocus(el, open)
    return open
  },

  // ALWAYS MOUNTED (white-label redesign 2026-09-26, DESIGN_SPEC §6.10): the
  // root used to flip `display: none` on close, so the EXIT snapped — no
  // transition can run out of `display: none`. Now the open state is the
  // `data-open` attribute — a FLAT `dataOpen` factory, never a `data: {}`
  // block a consumer's own `data` would replace (FW-ELEMENT-DEFINITION-DATA-
  // REPLACES-EXTENDS-DATA-1) — (reading `el.isOpen` here keeps syncUrl/manageFocus
  // firing) and every state is a STATIC selector rule: closed = transparent,
  // hidden and click-through, with the visibility held for the exit (`C`);
  // open = the veil fades in over `C` while the window rises on the spring.
  // The veil is the one blocking `backdrop` theme (scrim + blur) — it was the
  // bare `scrim` fill + a 4px blur.
  dataOpen: (el) => !!el.isOpen,
  display: 'flex',
  opacity: '0',
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: 'opacity B defaultBezier, visibility 0s linear C',
  '&[data-open]': {
    opacity: '1',
    visibility: 'visible',
    pointerEvents: 'auto',
    transition: 'opacity C defaultBezier, visibility 0s linear 0s'
  },

  position: 'fixed',
  inset: '0',
  zIndex: '2000',
  align: 'center center',
  theme: 'backdrop',
  padding: 'A',

  // THE PHONE SHEET (brand overlay law: "on a phone every blocking overlay
  // becomes the bottom sheet (top corners, grabber)" — the sheet the shared
  // AppDialog and the windows ModalLayer draw). `phoneSheet` is on by default;
  // `phoneSheet: false` keeps a Modal that is NOT a blocking overlay — a
  // dedicated page that reuses a modal as its renderer, an inline catalog
  // preview — on the inset phone frame it always had. The answer is published
  // as `data-phone-sheet` on the root, the Window and the grabber (FLAT
  // factories, the `dataOpen` rule above), and every phone-sheet rule is
  // keyed on it. Above `mobileL` nothing here applies: desktop is unchanged.
  dataPhoneSheet: (el) => el.scope.isPhoneSheet(el),
  // The frame half: the sheet meets the screen's edges, so the veil drops its
  // gutter. An opted-out Modal keeps the `Z` gutter.
  '@mobileL': {
    padding: 'Z',
    '&[data-phone-sheet]': { padding: '0' }
  },

  // Backdrop dismiss — listen on BOTH click and mousedown so the dismiss
  // fires reliably even when something between (a fade-out transition
  // mid-flight, or a child element's stopPropagation race) would
  // otherwise swallow the click. The mousedown handler covers the case
  // where DOMQL's click handler isn't getting the e.target it expects;
  // mousedown is fired before any other handler can rebind.
  //
  // `e.target === el.node` filters to the actual backdrop layer — clicks
  // inside the Window have e.target === the inner element, so they fall
  // through this guard and the Window's content gets the click.
  onClick: (e, el, s) => el.scope.maybeDismiss(e, el, s),
  onMousedown: (e, el, s) => el.scope.maybeDismiss(e, el, s),

  Window: {
    extends: 'ModalWindow',
    // Stop bubbling so clicks inside the Window don't reach the
    // backdrop dismiss handler above. ModalWindow doesn't ship a default
    // onClick — without this guard the dismiss check (`e.target ===
    // el.node`) is the only thing keeping window-internal clicks from
    // closing the modal, and Chrome's event re-targeting under
    // transforms or shadow DOM can break that check in subtle ways.
    onClick: (e) => e.stopPropagation(),
    onMousedown: (e) => e.stopPropagation(),
    // Tab / Shift+Tab stay inside the window — the AppDialog trap. Without it
    // Tab walked out to the page behind (ws-w2, CreateAgentModal) and the
    // root's Escape handler, which only hears keys from inside the modal,
    // went deaf. Focus moves in on open and back on close (manageFocus).
    onKeydown: (e, el) => el.call('appDialogTrapTab', e),
    boxSizing: 'border-box',
    // The centre-sheet motion (§6.4): rise 2.5% from 0.97 on the spring, leave
    // one step faster on the house curve. The pose is a custom property so
    // the phone pose below and the open rule never fight over `transform`.
    '--modal-from': 'translate3d(0, 2.5%, 0) scale(0.97)',
    transform: 'var(--modal-from)',
    transition: 'transform C defaultBezier',
    dataOpen: (el) => !!el.parent.isOpen,
    '&[data-open]': {
      transform: 'none',
      transition: 'transform D springBezier'
    },
    // manageFocus moves focus onto the Window; the brand reset's
    // [tabindex]:focus-visible ring (a box-shadow) must not replace its
    // elevation after a keyboard open — same rule as the AppDialog panel.
    ':focus-visible': { boxShadow: 'var(--shadow-overlay)' },
    // The overflow law's sheet frame: every floating overlay sits
    // `overlayGutter` in from the viewport on each side (it was one em-based
    // `A` in total — a different inset from every AppDialog sheet).
    dataPhoneSheet: (el) => el.scope.isPhoneSheet(el),
    '@mobileL': {
      width: '100%',
      maxWidth: 'calc(100dvw - 2 * overlayGutter)',
      maxHeight: 'calc(100dvh - 2 * overlayGutter)',
      padding: 'A',
      // Sheet presentation on mobile (DESIGN_LANGUAGE.md §7.5).
      round: 'radiusSheet',
      // the phone pose: the sheet rises from below
      '--modal-from': 'translate3d(0, 6.5%, 0)',
      // THE PHONE SHEET — the window half (the frame half is the root's own
      // `@mobileL` block): full width on the bottom edge, top corners only,
      // at most 92% of the screen so the veil above stays a dismiss target.
      // The same rule as the AppDialog phone panel:
      //   - `minWidth: '100%'` outranks any width or max-width, a consumer's
      //     own included, without a specificity fight;
      //   - `alignSelf` seats it on the bottom edge whatever the root's
      //     `align`;
      //   - the top corners through `round` itself, not the corner longhands
      //     (MobileRailSheet's form): it takes the same reactive-collision
      //     `!important` as the `round` above, so a consumer's reactive
      //     `round` (the ticket detail's) cannot keep the bottom corners;
      //   - THE HOME INDICATOR: a transparent bottom border the size of the
      //     safe-area inset — the surface paints under it, so the sheet meets
      //     the edge while the content ends above the system's swipe zone;
      //   - `position: 'relative'` makes the window the grabber's containing
      //     block (the brand ModalWindow sets it too);
      //   - THE GRABBER'S ZONE: the top inset goes back to `B` — the window's
      //     own desktop inset, the AppDialog head's phone inset and the
      //     windows sheet's — so the grabber at `Y` keeps clear of the first
      //     line (the phone `A` left it 5px). The sides keep the phone `A`.
      // The motion is the phone pose above: up 6.5% on the `D` spring, down on
      // `C` — the brand's `modalInUp` / `modalOutDown`, held as transitions
      // because this root is always mounted, so the exit still plays.
      '&[data-phone-sheet]': {
        position: 'relative',
        minWidth: '100%',
        maxHeight: '92%',
        alignSelf: 'flex-end',
        round: 'radiusSheet radiusSheet 0 0',
        borderBottomWidth: 'env(safe-area-inset-bottom)',
        borderBottomStyle: 'solid',
        borderBottomColor: 'transparent',
        paddingTop: 'B',
        transformOrigin: 'bottom center'
      }
    },
    // The phone sheet's grabber — decoration (the sheet closes by Escape, the
    // veil and Close), so aria-hidden and click-through. Absolute, so it takes
    // no flex gap and moves no content. Absent above `mobileL` and on an
    // opted-out Modal. The AppDialog grabber's tokens, so the sheets match.
    SheetGrabber: {
      ariaHidden: 'true',
      display: 'none',
      position: 'absolute',
      top: 'Y',
      left: '50%',
      width: 'grabberW',
      height: 'grabberH',
      round: 'radiusPill',
      background: 'line',
      transform: 'translateX(-50%)',
      pointerEvents: 'none',
      dataPhoneSheet: (el) => el.scope.isPhoneSheet(el),
      '@mobileL': { '&[data-phone-sheet]': { display: 'block' } }
    },
    // The name: the heading when there is one, else the consumer's label.
    ariaLabelledby: (el) => (el.scope.modalHasHeading(el) ? el.scope.modalTitleId(el) : null),
    ariaLabel: (el) => el.scope.modalRootLabel(el),
    onRender: (el) => el.scope.modalWarnUnnamed(el),
    ModalHeader: {
      Hgroup: {
        H: { id: (el) => el.scope.modalTitleId(el) }
      },
      // THE CLOSE BUTTON'S TOUCH AREA STAYS INSIDE THE WINDOW
      // (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1; the Finances lane's shell check).
      // The brand header pulls its Icons group into the window's padding
      // (`margin: -Y2 -Z1 - auto`), and on a coarse pointer the brand
      // IconButton grows a 6px tap slop (`:after`, inset tapSlop) around the
      // close button. On a phone the window's padding is `A`, so the slop
      // passed the window's edge and the window — a scroller — scrolled
      // sideways by 2px: measured on dev 501167cd5 with touch rules,
      // CreateAgentModal's Window scrollWidth 371 / clientWidth 369 at 393
      // (353/351 at 375, 418/416 at 440), the close button's right edge 3.8px
      // from the window's; with every `:after` off, 369/369. On a coarse
      // pointer the group pulls in by `Y` instead, so the slop ends inside the
      // padding; a mouse keeps the brand's alignment.
      Icons: { '@coarse': { marginRight: '-Y' } }
    }
  }
}
